<!-- src/components/student/StudentCoursesSlider.vue -->
<template>
  <div class="slider-section">
    <div class="slider-container">
      <div class="slider-wrapper">
        <!-- Заголовок и стрелки -->
        <div class="slider-header">
          <div class="slider-header-left">
            <h2 class="slider-title">{{ title }}</h2>
            <slot name="header-actions"></slot>
          </div>
          <div v-if="courses.length > 0" class="slider-arrows">
            <button 
              class="arrow-btn" 
              :disabled="!canScrollLeft" 
              @click="scroll('left')"
            >
              ←
            </button>
            <button 
              class="arrow-btn" 
              :disabled="!canScrollRight" 
              @click="scroll('right')"
            >
              →
            </button>
          </div>
        </div>

        <!-- Загрузка -->
        <LoadingSpinner v-if="loading" text="Загрузка курсов..." />

        <!-- Нет курсов -->
        <div v-else-if="courses.length === 0" class="no-courses-message">
          <p>
            {{ searchQuery
              ? 'Курсы не найдены. Попробуйте изменить запрос.'
              : 'Курсы пока не добавлены.'
            }}
          </p>
        </div>

        <!-- Трек с карточками -->
        <div 
          v-else 
          class="slider-track-wrapper"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
        >
          <div
            class="slider-track"
            ref="trackRef"
            :style="{ transform: `translateX(${translateX}px)` }"
          >
            <CourseCard
              v-for="course in courses"
              :key="course.id"
              :course="course"
              @click="onCourseClick"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue'
import CourseCard from '@/components/shared/CourseCard.vue'

export default {
  name: 'StudentCoursesSlider',

  components: {
    LoadingSpinner,
    CourseCard,
  },

  props: {
    title: {
      type: String,
      default: 'Рекомендовано вам',
    },
    courses: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    searchQuery: {
      type: String,
      default: '',
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    totalPages: {
      type: Number,
      default: 1,
    },
  },

  emits: ['go-to-course', 'prev-page', 'next-page', 'go-to-page'],

  setup(props) {
    const trackRef = ref(null)

    // ========== СОСТОЯНИЕ ==========
    const currentSlide = ref(0)
    const translateX = ref(0)
    const touchStartX = ref(0)
    const touchEndX = ref(0)
    const isSwiping = ref(false)
    const containerWidth = ref(0)

    // ========== COMPUTED ==========

    const totalSlides = computed(() => props.courses.length)

    // Сколько карточек видно одновременно
    const visibleCards = computed(() => {
      if (containerWidth.value === 0) return 4
      
      const cardWidth = 385
      const gap = 24
      
      // Вычисляем сколько ПОЛНЫХ карточек помещается
      const visible = Math.floor((containerWidth.value + gap) / (cardWidth + gap))
      return Math.max(1, visible)
    })

    // Максимальный индекс слайда - чтобы последняя карточка была видна
    const maxSlide = computed(() => {
      if (totalSlides.value <= visibleCards.value) {
        // Все карточки помещаются, листать не нужно
        return 0
      }
      // Последняя позиция: когда последняя карточка видна справа
      // Добавляем +1 чтобы гарантировать видимость последней карточки
      const max = totalSlides.value - visibleCards.value
      return Math.max(0, max)
    })

    const canScrollLeft = computed(() => 
      currentSlide.value > 0
      // Убрал переход на предыдущую страницу: || props.currentPage > 1
    )

    const canScrollRight = computed(() => 
      currentSlide.value < maxSlide.value
      // Убрал переход на следующую страницу: || props.currentPage < props.totalPages
    )

    // ========== МЕТОДЫ ==========

    const measureContainer = () => {
      if (!trackRef.value) return
      const wrapper = trackRef.value.parentElement
      if (wrapper) {
        containerWidth.value = wrapper.offsetWidth
      }
    }

    const updateTransform = () => {
      const cardWidth = 385
      const gap = 24
      const offset = currentSlide.value * (cardWidth + gap)
      translateX.value = -offset
    }

    const handleTouchStart = (event) => {
      touchStartX.value = event.touches[0].clientX
      isSwiping.value = true
    }

    const handleTouchMove = (event) => {
      if (!isSwiping.value) return
      touchEndX.value = event.touches[0].clientX
    }

    const handleTouchEnd = () => {
      if (!isSwiping.value) return
      
      const swipeDistance = touchStartX.value - touchEndX.value
      const minSwipeDistance = 50
      
      if (swipeDistance > minSwipeDistance) {
        nextSlide()
      } else if (swipeDistance < -minSwipeDistance) {
        prevSlide()
      }
      
      isSwiping.value = false
    }

    const nextSlide = () => {
      if (currentSlide.value < maxSlide.value) {
        currentSlide.value++
        updateTransform()
      }
      // Убрал автоматический переход на следующую страницу
    }

    const prevSlide = () => {
      if (currentSlide.value > 0) {
        currentSlide.value--
        updateTransform()
      }
      // Убрал автоматический переход на предыдущую страницу
    }

    const _goToSlide = (slideIndex) => {
      // Ограничиваем индекс максимальной позицией
      const targetSlide = Math.min(slideIndex, maxSlide.value)
      currentSlide.value = Math.max(0, targetSlide)
      updateTransform()
    }

    const scroll = (direction) => {
      if (direction === 'left') {
        prevSlide()
      } else {
        nextSlide()
      }
    }

    // Сброс при смене страницы
    const resetSlider = () => {
      currentSlide.value = 0
      updateTransform()
    }

    const handleResize = () => {
      measureContainer()
      // Пересчитываем позицию с учётом новых размеров
      if (currentSlide.value > maxSlide.value) {
        currentSlide.value = maxSlide.value
      }
      updateTransform()
    }

    // ========== WATCHERS ==========

    watch(() => props.currentPage, () => {
      nextTick(() => {
        resetSlider()
      })
    })

    watch(() => props.courses, () => {
      nextTick(() => {
        measureContainer()
        // Если текущая позиция больше максимальной, корректируем
        if (currentSlide.value > maxSlide.value) {
          currentSlide.value = maxSlide.value
          updateTransform()
        }
      })
    }, { deep: true })

    // ========== LIFECYCLE ==========

    onMounted(() => {
      nextTick(() => {
        measureContainer()
        updateTransform()
        window.addEventListener('resize', handleResize, { passive: true })
      })
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize)
    })

    return {
      trackRef,
      translateX,
      canScrollLeft,
      canScrollRight,
      scroll,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
    }
  },

  methods: {
    onCourseClick(courseId) {
      this.$emit('go-to-course', courseId)
    },
  },
}
</script>

<style scoped>
.slider-section {
  background: #e7f3ff;
  padding: 40px 0 60px;
  width: 100%;
}

.slider-container {
  padding: 0 40px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.slider-wrapper {
  max-width: 1800px;
  margin: 0 auto;
  background: white;
  border-radius: 24px;
  padding: 50px 60px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.slider-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
}

.slider-header-left {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
}

.slider-title {
  font-size: 32px;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
}

.slider-arrows {
  display: flex;
  gap: 12px;
}

.arrow-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid #e2e8f0;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  font-size: 20px;
  color: #64748b;
  user-select: none;
}

.arrow-btn:hover:not(:disabled) {
  background: #3b82f6;
  border-color: #3b82f6;
  color: white;
  transform: scale(1.05);
}

.arrow-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.slider-track-wrapper {
  position: relative;
  overflow: hidden;
  touch-action: pan-y;
}

.slider-track {
  display: flex;
  gap: 24px;
  transition: transform 0.3s ease;
  padding: 15px 5px;
  will-change: transform;
}

.scroll-indicator {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
}

.indicator-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e2e8f0;
  cursor: pointer;
  transition: all 0.3s ease;
}

.indicator-dot:hover {
  background: #94a3b8;
  transform: scale(1.2);
}

.indicator-dot.active {
  width: 24px;
  border-radius: 4px;
  background: #3b82f6;
}

.no-courses-message {
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
  font-size: 16px;
}

@media (max-width: 768px) {
  .slider-container {
    padding: 0 20px;
  }

  .slider-wrapper {
    padding: 30px 20px;
  }

  .slider-title {
    font-size: 24px;
  }

  .slider-header {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
}
</style>
